import React, { useState, useEffect } from 'react';
import { X, ShieldAlert, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const StatusModal = ({ currentStatus, recordId, domainId, appName, onClose, onComplete }) => {
  const { t } = useTranslation(); // 🎯 تفعيل الـ Hook
  
  const [memo, setMemo] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [isLoadingDomain, setIsLoadingDomain] = useState(false);

  const activeDomain = domainId || (appName === 'ASSET' ? 'ASSETSTATUS':  appName === 'ITEMS' ? 'ITEMSTATUS' : appName === 'INVUSAGE' ? 'INVUSAGESTATUS' : appName === 'MR' ? 'MRSTATUS' : appName === 'PR' ? 'PRSTATUS' : appName === 'PO' ? 'POSTATUS' : appName === 'INVENTORY' ? 'INVSTATUS' : appName === 'WO' ? 'WOSTATUS' : appName === 'WOTASK' ? 'WOSTATUS' : appName == 'JOBPLAN' ? 'JPSTATUS' : appName === 'PM' ? 'PMSTATUS' : appName === 'RFQ' ? 'RFQSTATUS' : appName === 'SLA' ? 'SLASTATUS' : appName === 'TOOL' ? 'TOOLSTATUS' : 'LOCSTATUS');
  
  useEffect(() => {
    const fetchDomainValues = async () => {
      setIsLoadingDomain(true);
      try {
        const res = await api.get(`/domains/${activeDomain}`);
        
        if (res.data && res.data.values) {
          setAvailableStatuses(res.data.values); 
        } else if (Array.isArray(res.data)) {
          setAvailableStatuses(res.data);
        } else {
          setAvailableStatuses([]);
        }
        
        const filtered = res.data.values ? res.data.values.filter(s => s.value !== currentStatus) : [];
        if (filtered.length === 1) {
          setNewStatus(filtered[0].value);
        }
        
      } catch (err) {
        console.error("Fetch Error:", err);
        toast.error(t('statusModal.loadError')); // 🎯
        setAvailableStatuses([]);
      } finally {
        setIsLoadingDomain(false);
      }
    };

    fetchDomainValues();
  }, [activeDomain, currentStatus, t]);

  const handleUpdate = async () => {
      if (!newStatus) {
          return toast.error(t('statusModal.selectFirst')); // 🎯
      }
  
      const loadId = toast.loading(t('statusModal.updating')); // 🎯
      
      try {
          const res = await api.post('/status/change', {
              record_id: recordId,
              new_status: newStatus,
              memo: memo,
              app_name: appName,
              domain_id: activeDomain
          });
          
          toast.success(t('statusModal.success'), { id: loadId }); // 🎯
          if (onComplete) onComplete();
          onClose();
      } catch (err) {
          const detail = err.response?.data?.detail;
          const msg = Array.isArray(detail) ? detail[0].msg : (detail || t('statusModal.failed')); // 🎯
          toast.error(String(msg), { id: loadId });
      }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9000] animate-in fade-in duration-200">
      <div className="bg-white rounded-lg shadow-2xl w-[420px] border border-gray-200 flex flex-col overflow-hidden animate-in zoom-in-95">
        
        {/* Header */}
        <div className="bg-[#f8f9fa] p-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-2 text-[#005495]">
            <ShieldAlert size={18} />
            <span className="font-black uppercase tracking-tighter text-[11px]">{t('statusModal.changeStatus')} {recordId}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors">
            <X size={20}/>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Current Status Box */}
          <div className="bg-blue-50/50 p-3 rounded border-l-4 border-blue-500 shadow-inner">
            <label className="text-blue-400 font-bold text-[9px] uppercase block mb-1">{t('statusModal.currentStatus')}</label>
            <div className="font-black text-blue-900 text-sm italic">{currentStatus}</div>
          </div>

          {/* New Status Select */}
          <div className="flex flex-col gap-1">
            <label className="text-blue-600 font-bold text-[9px] uppercase tracking-widest">{t('statusModal.availableStatuses')}</label>
            <div className="relative">
              <select 
                className="w-full p-2.5 border border-gray-300 rounded-sm bg-white font-bold text-gray-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all appearance-none text-[12px]"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                disabled={isLoadingDomain}
              >
                <option value="">{isLoadingDomain ? t('statusModal.loading') : t('statusModal.chooseStatus')}</option>
                
                {availableStatuses
                .filter(s => s.value !== currentStatus)
                .map((s, i) => (
                  <option key={i} value={s.value}>
                    {s.value}
                  </option>
                ))}
              </select>

              {isLoadingDomain && (
                <div className="absolute right-8 top-3">
                  <Loader2 size={14} className="animate-spin text-blue-500" />
                </div>
              )}
            </div>
          </div>

          {/* Memo Area */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-400 font-bold text-[9px] uppercase tracking-widest font-mono">{t('statusModal.memo')}</label>
            <textarea 
              className="w-full p-2 border border-gray-200 rounded-sm bg-gray-50/30 text-[11px] h-20 outline-none focus:border-blue-400 focus:bg-white transition-all resize-none shadow-inner"
              placeholder={t('statusModal.enterNotes')}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 flex justify-end gap-3 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-500 font-bold uppercase text-[10px] hover:text-gray-700">{t('statusModal.cancel')}</button>
          <button 
            onClick={handleUpdate} 
            disabled={!newStatus || isLoadingDomain}
            className="bg-[#005495] text-white px-10 py-2 rounded-sm shadow-lg font-black uppercase text-[10px] hover:bg-[#003d6d] disabled:opacity-50 transition-all"
          >
            {t('statusModal.update')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusModal;